import React, { useEffect } from "react"
import { Badge, Card, CardBody, CardTitle, Col, Row, Table } from "reactstrap"
import { useSelector } from "react-redux"
import { useDispatch } from "react-redux"
import { useLocation, useNavigate } from "react-router-dom"
import Breadcrumbs from "components/Common/Breadcrumb"
import BackButton from "components/Shared/BackButton"
import { ShimmerThumbnail } from "react-shimmer-effects"
import { getCountryDetails } from "store/actions/countriesAction"
import isPermissionEnabled from "common/isPermissionEnabled"
import { isPlatformCountryManager } from "utils/platformCountryAccess"
import { getCountriesFallbackDashboard } from "utils/countriesRoutes"
import { useTranslation } from "react-i18next"
import LtrText from "components/Shared/LtrText"
import { getCurrencyLabel } from "utils/currency"

const CountryDetails = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()

  document.title = t(`Country Details `)

  const location = useLocation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const searchParams = new URLSearchParams(location.search)
  const id = searchParams.get("id")

  const { loading, countryDetails } = useSelector(state => state.Countries)

  const { userRole, accountType, accountScope } = useSelector(state => state.Login)

  const isPlatform = isPlatformCountryManager({ accountType, accountScope })
  // Tax details are tenant-scoped — only show on tenant side.
  // Shipment Details temporarily disabled — not used in live delivery fee logic.
  const showTaxShipmentDetails = !isPlatform

  // const regionTypeOptions = [
  //   { label: t("Local"), value: "local" },
  //   { label: t("International"), value: "international" },
  // ]

  // const priorityLogicOptions = [
  //   { label: t("Actual Weight"), value: "actual_weight" },
  //   { label: t("Dynamic Weight"), value: "dynamic_weight" },
  //   { label: t("Max of both"), value: "max_of_both" },
  // ]

  useEffect(() => {
    const canManageCountries = isPlatformCountryManager({
      accountType,
      accountScope,
    })
    const isPermissionView = isPermissionEnabled(
      userRole,
      11,
      "country_1",
      accountType,
      accountScope
    )
    if (!isPermissionView && !canManageCountries) {
      navigate(getCountriesFallbackDashboard({ accountType, accountScope }))
    }
  }, [userRole, accountType, accountScope, navigate])

  useEffect(() => {
    const fetchOldDetails = () => {
      const payload = {
        id,
      }
      dispatch(getCountryDetails(payload))
    }
    fetchOldDetails()
  }, [])

  return (
    <div className="page-content">
      <div className="container-fluid">
        <Breadcrumbs />
        <Row>
          <Col lg={12}>
            <Card>
              <CardBody>
                <CardTitle className="d-flex align-items-center">
                  <BackButton /> {t("Country Details")}
                </CardTitle>
                <p className="text-muted mb-4"></p>
                <div className="table-responsive">
                  {loading ? (
                    <ShimmerThumbnail height={650} rounded />
                  ) : Object.values(countryDetails).length === 0 ? (
                    <p className="text-center my-2">
                      {t("No country details found")}
                    </p>
                  ) : (
                    <Table className="table-nowrap mb-0 capitalize_td">
                      <tbody>
                        <tr>
                          <th scope="row">{t("Name(English)")}</th>
                          <td>{countryDetails?.name}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Name(عربي)")}</th>
                          <td>{countryDetails?.nameAr}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Country Code")}</th>
                          <td>{countryDetails.countryCode || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Dial Code")}</th>
                          <td>
                            {countryDetails.dialCode ? (
                              <LtrText>{countryDetails.dialCode}</LtrText>
                            ) : (
                              "-"
                            )}
                          </td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Phone Number Length")}</th>
                          <td>{countryDetails.phoneNumberLength || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Display Order")}</th>
                          <td>{countryDetails.displayOrder || "-"}</td>
                        </tr>
                        <tr>
                          <th scope="row">{t("Currency")}</th>
                          <td>
                            {countryDetails.currency
                              ? getCurrencyLabel(countryDetails.currency)
                              : "-"}
                          </td>
                        </tr>
                        {showTaxShipmentDetails && (
                          <>
                            <tr>
                              <th scope="row">{t("Tax Title(English)")}</th>
                              <td>{countryDetails?.taxInfo?.title || "-"}</td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Tax Title(عربي)")}</th>
                              <td>{countryDetails?.taxInfo?.titleAr || "-"}</td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Tax Value")}</th>
                              <td>{countryDetails?.taxInfo?.value || "-"}</td>
                            </tr>
                            <tr>
                              <th scope="row">{t("Tax Registered No")}</th>
                              <td>
                                {countryDetails?.taxInfo?.registeredNo || "-"}
                              </td>
                            </tr>
                          </>
                        )}
                        <tr>
                          <th scope="row">{t("Status")}</th>
                          <td>
                            {countryDetails?.status === 1 ? (
                              <Badge className="bg-success">
                                {t("Active")}
                              </Badge>
                            ) : (
                              <Badge className="bg-danger">
                                {t("Deactive")}
                              </Badge>
                            )}
                          </td>
                        </tr>

                        {/* Shipment Info Block temporarily disabled — not used in live delivery fee logic. */}
                        {/* {showTaxShipmentDetails &&
                          countryDetails?.shipmentInfo && (
                          <>
                            <tr>
                              <th colSpan={2} className="table-active">
                                {t("Shipment Information")}
                              </th>
                            </tr>
                            <tr>
                              <th>{t("Region Type")}</th>
                              <td>
                                {regionTypeOptions.find(
                                  item =>
                                    item.value ===
                                    countryDetails.shipmentInfo.regionType
                                )?.label || "-"}
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Fixed Delivery Charge")}</th>
                              <td>
                                {
                                  countryDetails.shipmentInfo
                                    .fixedDeliveryCharge
                                }
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Free Delivery Threshold")}</th>
                              <td>
                                {
                                  countryDetails.shipmentInfo
                                    .freeDeliveryThreshold
                                }
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Enable Free Delivery")}</th>
                              <td>
                                {countryDetails.shipmentInfo.enableFreeDelivery
                                  ? t("Yes")
                                  : t("No")}
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Actual Base Weight Limit")}</th>
                              <td>
                                {
                                  countryDetails.shipmentInfo
                                    .actualBaseWeightLimit
                                }
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Actual Base Rate")}</th>
                              <td>
                                {countryDetails.shipmentInfo.actualBaseRate}
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Actual Extra Per KG Rate")}</th>
                              <td>
                                {
                                  countryDetails.shipmentInfo
                                    .actualExtraPerKgRate
                                }
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Dynamic Base Weight Limit")}</th>
                              <td>
                                {
                                  countryDetails.shipmentInfo
                                    .dynamicBaseWeightLimit
                                }
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Dynamic Base Rate")}</th>
                              <td>
                                {countryDetails.shipmentInfo.dynamicBaseRate}
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Dynamic Extra Per KG Rate")}</th>
                              <td>
                                {
                                  countryDetails.shipmentInfo
                                    .dynamicExtraPerKgRate
                                }
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Enable Box Fee")}</th>
                              <td>
                                {countryDetails.shipmentInfo.enableBoxFee
                                  ? t("Yes")
                                  : t("No")}
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Box Fee")}</th>
                              <td>{countryDetails.shipmentInfo.boxFee}</td>
                            </tr>
                            <tr>
                              <th>{t("No of Base Items Limit")}</th>
                              <td>
                                {countryDetails.shipmentInfo.noOfBaseItemsLimit}
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Base Fulfillment Fee")}</th>
                              <td>
                                {countryDetails.shipmentInfo.baseFulfillmentFee}
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Extra Item Fee")}</th>
                              <td>
                                {countryDetails.shipmentInfo.extraItemFee}
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Enable Surcharge")}</th>
                              <td>
                                {countryDetails.shipmentInfo.enableSurcharge
                                  ? t("Yes")
                                  : t("No")}
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Surcharge Percentage")}</th>
                              <td>
                                {countryDetails.shipmentInfo.surchargeRate}
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Enable Dynamic Calc")}</th>
                              <td>
                                {countryDetails.shipmentInfo.enableDynamicCalc
                                  ? t("Yes")
                                  : t("No")}
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Volumetric Divisor")}</th>
                              <td>
                                {countryDetails.shipmentInfo.volumetricDivisor}
                              </td>
                            </tr>
                            <tr>
                              <th>{t("Priority Logic")}</th>
                              <td>
                                {(() => {
                                  const match = priorityLogicOptions.find(
                                    i =>
                                      i.value ===
                                      countryDetails.shipmentInfo.priorityLogic
                                  )
                                  return match?.label ? t(match.label) : "-"
                                })()}
                              </td>
                            </tr>
                          </>
                        )} */}
                      </tbody>
                    </Table>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    </div>
  )
}

export default CountryDetails
